import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { ResourceLinks } from "../../../../constants";
import { makeArrayRandom } from "../../../../utils/learner";
import { useSelector } from "../../../../store";

export default function WebMatchTextSelection({ question, onAnswer = () => {} }) {
    const questionData = question;
    const learnerState = useSelector((state) => state.learner || {});
    const data = learnerState?.details?.testLevel === "vfh" ? questionData?.vfh : questionData?.acsf4;
    const imagesResources = ResourceLinks.llnQuizImage;


    const [websiteChoices, setWebsiteChoices] = useState({
        organisation: "",
        australian: "",
        company: "",
        government: "",
    });

    const [selectedChoice, setSelectedChoice] = useState(null);
    const [activeSelect, setActiveSelect] = useState(false);
    const [selectedKeyPrevious, setSelectedKeyPrevious] = useState(null);

    const [availableChoices, setAvailableChoices] = useState(
        makeArrayRandom(data.choices)
    );

    // every time question change we will set to reset needed to reset
    useEffect(() => {
        setWebsiteChoices({
            organisation: "",
            australian: "",
            company: "",
            government: "",
        });
        setSelectedChoice(null);
        setActiveSelect(false);
        setSelectedKeyPrevious(null);
        setAvailableChoices(makeArrayRandom(data.choices));
    }, [question,
        data.choices
    ]);

    const mapWebsiteKeyToChoices = (websiteKey) => {
        switch (websiteKey) {
            case "Government Website":
                return "government";
            case "Company Website":
                return "company";
            case "Australian Website":
                return "australian";
            case "Organisation Website":
                return "organisation";
            default:
                return "";
        }
    };

    const handleChoiceClick = (choice) => {
        setSelectedChoice(choice);
        setActiveSelect(true);
        setSelectedKeyPrevious(null);
    };

    const handleWebsiteClick = (websiteKey) => {
        if (selectedChoice) {
            const mappedKey = mapWebsiteKeyToChoices(websiteKey);

            if (mappedKey) {
                let currentWebsiteChoices = {...websiteChoices};
                const previousChoice = currentWebsiteChoices[mappedKey];

                if (previousChoice) {
                    setAvailableChoices((prevChoices) => [
                        ...prevChoices,
                        { value: previousChoice },
                    ]);
                }

                // we will check here if coming from previous answer
                if (selectedKeyPrevious) {
                    currentWebsiteChoices = {
                        ...currentWebsiteChoices,
                        [selectedKeyPrevious]: "",
                    }
                    setSelectedKeyPrevious(null);
                }
                // updating answer
                currentWebsiteChoices = {
                    ...currentWebsiteChoices,
                    [mappedKey]: selectedChoice.value,
                }
                setWebsiteChoices(currentWebsiteChoices);

                setAvailableChoices((prevChoices) =>
                    prevChoices.filter(
                        (item) => item.value !== selectedChoice.value
                    )
                );

                if (
                    typeof questionData.answer.value === "object" &&
                    !Array.isArray(questionData.answer.value)
                ) {

                    const answer = {
                        value: {
                            ...currentWebsiteChoices,
                            [mappedKey]: selectedChoice.value,
                        },
                    };
                    
                    const canProceed = true;
                    onAnswer({
                        canProceed,
                        answer: answer.value,
                    });
                }

                setSelectedChoice(null);
                setActiveSelect(false);
            }
        }
    };

    const handleRemoveChoice = (websiteKey) => {
        const mappedKey = mapWebsiteKeyToChoices(websiteKey);
        const removedChoice = websiteChoices[mappedKey];
        if (removedChoice) {
            setWebsiteChoices((prevChoices) => ({
                ...prevChoices,
                [mappedKey]: "",
            }));

            setAvailableChoices((prevChoices) => [
                ...prevChoices,
                { value: removedChoice },
            ]);

            const answer = {
                value: {
                    ...websiteChoices,
                    [mappedKey]: "",
                },
            };

            let canProceed = false;
            const answerKeys = Object.keys(answer.value);
            answerKeys.map((keys) => {
                if (answer.value[keys] !== "" && answer.value[keys] !== null) {
                    canProceed = true;
                }
                return null;
            });
            
            onAnswer({
                canProceed,
                answer: answer.value,
            });
        }
    };

    const handleAnswerClick = (websiteKey) => {
        const mappedKey = mapWebsiteKeyToChoices(websiteKey);
        const choice = data.choices.find((c) => c.value === websiteChoices[mappedKey]);
        setSelectedChoice(choice);
        setActiveSelect(true);
        setSelectedKeyPrevious(mappedKey);
    }

    return (
        <Box
            sx={{
                width: "1100px",
                margin: "0 auto",
                padding: "20px",
                backgroundColor: "white",
            }}
        >
            <Box
                sx={{
                    width: "914px",
                    margin: "0 auto",
                    padding: "20px 20px 0 20px",
                    border: "1px dashed lightgrey",
                }}
            >
                <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
                    {availableChoices.map((choice, index) => (
                        <Grid item xs={3} key={index} sx={{ width: "25%" }}>
                            <Button
                                onClick={() => handleChoiceClick(choice)}
                                disabled={selectedChoice === choice}
                                fullWidth
                                disableRipple
                                className={"selection-button-content " + (selectedChoice?.value === choice.value ? "choice-button-selected" : "")}
                                sx={{
                                    borderBottom:
                                        "10px solid rgb(52, 148, 186)",
                                    marginRight: "5px",
                                    marginBottom: "5px",
                                    height: "42px",
                                    width: "206px",
                                    color: "black !important",
                                    backgroundColor:
                                        "rgb(170, 214, 231) !important",
                                    lineHeight: "0.8em",
                                    fontWeight: "700",
                                    fontSize: "22px",
                                }}
                            >
                                {choice.value}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box
                sx={{
                    width: "550px",
                    height: "420px",
                    aspectRatio: "550 / 420",
                    margin: "0 auto",
                    padding: "20px",
                    marginTop: "20px",
                    backgroundImage: `url(${imagesResources}${data.image})`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Grid
                    container
                    spacing={2}
                    sx={{
                        width: "100%",
                        paddingLeft: "4px",
                    }}
                >
                    {data.websites && data.websites.map((websiteKey, index) => (
                        <Grid
                            item
                            xs={6}
                            key={index}
                            sx={{
                                width: "50%",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Box className={"selection-button-border " + (activeSelect ? "choice-button-selected" : "")}
                                sx={{
                                    height: "53px",
                                    width: "232px",
                                    marginRight: "5px",
                                    marginBottom: "5px",
                                    backgroundColor: websiteChoices[
                                        mapWebsiteKeyToChoices(websiteKey)
                                    ]
                                        ? "rgb(170, 214, 231)"
                                        : "white",
                                    boxShadow: activeSelect
                                        ? "rgb(199, 0, 199) 0px 0px 0px 3px"
                                        : "none",
                                    transition: "box-shadow 0.2s ease-in-out",
                                    borderTop: websiteChoices[
                                        mapWebsiteKeyToChoices(websiteKey)
                                    ]
                                        ? "none"
                                        : "10px solid rgb(91, 105, 106)",
                                }}
                                onClick={() => handleWebsiteClick(websiteKey)}
                            >
                                {websiteChoices[
                                    mapWebsiteKeyToChoices(websiteKey)
                                ] ? (
                                    <Box
                                        sx={{
                                            position: "relative",
                                            width: "100%",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: "bold",
                                                textAlign: "center",
                                                fontSize: "20px",
                                                lineHeight: "2em",
                                            }}
                                            onClick={() => {handleAnswerClick(websiteKey)}}
                                        >
                                            {
                                                websiteChoices[
                                                    mapWebsiteKeyToChoices(
                                                        websiteKey
                                                    )
                                                ]
                                            }
                                        </Typography>
                                        <Box className="selected-close-btn"
                                            style={{
                                                top: "-4px",
                                                right: "-4px",
                                                padding: "4px",
                                                fontWeight: "bold",
                                                width: "17.33px",
                                                height: "20px",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveChoice(websiteKey);
                                            }}
                                        >
                                            X
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            fontSize: "20px",
                                        }}
                                    >
                                        {websiteKey}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};
